import { useState, useEffect } from 'react';
import { Card, Button, Badge, getAuthToken } from '@trafiflow/shared-ui';
import { Plus, Edit2, Trash2, Shield, Loader2 } from 'lucide-react';
import { API_CONFIG } from '../../../shared/config';

interface Role {
    id: string;
    name: string;
    parentId?: string;
}

export default function Roles() {
    const [roles, setRoles] = useState<Role[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);

    // Form state
    const [roleName, setRoleName] = useState('');
    const [parentRoleId, setParentRoleId] = useState('');

    const fetchRoles = async () => {
        setIsLoading(true);
        try {
            const token = await getAuthToken();
            const response = await fetch(`${API_CONFIG.baseUrl}/org/roles`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                console.error('Error fetching roles: HTTP', response.status, response.statusText);
                const errorText = await response.text();
                console.error('Error response:', errorText);
                return;
            }
            
            const result = await response.json();
            console.log('Roles API response:', result);
            
            if (result.success) {
                console.log('Roles found:', result.data?.length || 0);
                setRoles(result.data || []);
            } else {
                console.error('Roles API returned success=false:', result);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const handleOpenModal = (role?: Role) => {
        if (role) {
            setEditingRole(role);
            setRoleName(role.name);
            setParentRoleId(role.parentId || '');
        } else {
            setEditingRole(null);
            setRoleName('');
            setParentRoleId('');
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!roleName) return;
        setIsSaving(true);
        try {
            const token = await getAuthToken();
            // The backend CreateRoleHandler handles saving. For updates, we can either implement UpdateRoleHandler or use the same POST logic if it's idempotent.
            // Let's use the same POST endpoint for simplicity if it handles overwrite

            const response = await fetch(`${API_CONFIG.baseUrl}/org/roles`, {
                method: 'POST', // We use POST for both create and update for now based on backend impl
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: editingRole?.id,
                    name: roleName,
                    parentId: parentRoleId || undefined
                })
            });

            const result = await response.json();
            if (result.success) {
                await fetchRoles();
                setIsModalOpen(false);
            }
        } catch (error) {
            console.error('Error saving role:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this role?')) return;

        try {
            const token = await getAuthToken();
            const response = await fetch(`${API_CONFIG.baseUrl}/org/roles/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.success) {
                setRoles(roles.filter((r: Role) => r.id !== id));
            }
        } catch (error) {
            console.error('Error deleting role:', error);
        }
    };

    const getParentName = (parentId?: string) => {
        if (!parentId) return '-';
        return roles.find((r: Role) => r.id === parentId)?.name || 'Unknown';
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-tf-text">Organization Roles</h1>
                    <p className="text-sm text-tf-text-muted">Manage your company's hierarchy and business roles</p>
                </div>
                <Button onClick={() => handleOpenModal()} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Role
                </Button>
            </div>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-tf-bg-muted border-b border-tf-border">
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Role Name</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Parent Role</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider">Identifier</th>
                                <th className="px-6 py-3 text-xs font-semibold text-tf-text-muted uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-tf-border">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-10 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                            <span className="text-sm text-tf-text-muted">Loading roles...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : roles.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-tf-text-muted">
                                        No roles found. Create one to get started.
                                    </td>
                                </tr>
                            ) : (
                                roles.map((role: Role) => (
                                    <tr key={role.id} className="hover:bg-tf-bg-muted transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded bg-primary-lighter flex items-center justify-center text-primary">
                                                    <Shield className="w-4 h-4" />
                                                </div>
                                                <span className="font-medium text-tf-text">{role.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {role.parentId ? (
                                                <Badge variant="info">{getParentName(role.parentId)}</Badge>
                                            ) : (
                                                <span className="text-tf-text-muted italic text-sm">Top Level</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 font-mono text-xs text-tf-text-muted">
                                            {role.id}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(role)}
                                                    className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary-lighter rounded transition-all"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(role.id)}
                                                    className="p-2 text-tf-text-muted hover:text-critical hover:bg-critical-lighter rounded transition-all"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Modal Mockup - Using absolute positioning for demo purposes since we don't have a shared Modal component yet */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <Card className="w-full max-w-md p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <h2 className="text-xl font-bold mb-4">{editingRole ? 'Edit Role' : 'New Role'}</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-tf-text mb-1">Role Name</label>
                                <input
                                    type="text"
                                    value={roleName}
                                    onChange={(e) => setRoleName(e.target.value)}
                                    className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                    placeholder="e.g. Finance Manager"
                                    disabled={isSaving}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-tf-text mb-1">Parent Role (Optional)</label>
                                <select
                                    value={parentRoleId}
                                    onChange={(e) => setParentRoleId(e.target.value)}
                                    className="w-full px-3 py-2 border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                    disabled={isSaving}
                                >
                                    <option value="">None (Top Level)</option>
                                    {roles.filter(r => r.id !== editingRole?.id).map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={isSaving}>Cancel</Button>
                                <Button onClick={handleSave} disabled={isSaving || !roleName}>
                                    {isSaving ? (
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </div>
                                    ) : (editingRole ? 'Update Role' : 'Create Role')}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
